import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { authnClient } from '@frontend/auth';
import { AuthStorage, localStorageHelper, isWeaveTokenActive, isWeaveTokenBufferActive } from '@frontend/auth-helpers';
import { useFetch } from '@frontend/fetch';
import { useLocationService } from '@frontend/wam-location-service';
import { useAlert } from '@frontend/design-system';

export const useQueryUser = (enabled: boolean) => {
  return useQuery({
    queryKey: 'user',
    queryFn: async () => {
      const lsUser = localStorageHelper.get<Awaited<ReturnType<typeof authnClient.fetchUser>>>(AuthStorage.user);
      if (lsUser) {
        return lsUser;
      } else {
        return authnClient.fetchUser();
      }
    },
    enabled,
  });
};

/* 
Put this in callbacks to prevent causing a 
large number of re-renders by having it called in the AuthProvider.
*/
export const useHttpSetupOnce = () => {
  const alerts = useAlert();
  const {
    setAuthorizationHeader,
    // useMiddleware,
    // clearMiddleware,
    // useErrorMiddleware,
    // clearErrorMiddleware,
    setLocationIdHeader,
  } = useFetch();

  const { currentLocation } = useLocationService();

  /* Prevents the setupHttp function from being called more than once */
  const setupCalled = useRef(false);

  //@ts-ignore
  const _refreshTokenMiddleware = async (req: Request) => {
    /* If this request does not need a token, just continue with the request */
    if (!req.headers.has('Authorization')) {
      return req;
    }

    /**
     *
     * OKTA
     * If okta and weave are active, then follow through with request
     *
     * If okta active and weave inactive, then renew weave token
     *
     * If okta inactive and weave active - sign out
     *
     * If okta inactive and weave inactive - sign out
     *
     *
     * LEGACY
     *
     * if weave token active - follow through
     * if weave token inactive - renew
     */

    if (!authnClient.isUserAuthenticated() && !isWeaveTokenBufferActive()) {
      alerts.error('You have been signed out due to inactivity. Please sign in again');
      location.href = '/logout';
      return req;
    }

    /* If the token has expired, refresh it and update the request header and http instance */
    if (!isWeaveTokenActive() && isWeaveTokenBufferActive()) {
      try {
        const newToken = await authnClient.refreshWeaveToken();
        req.headers.set('Authorization', `Bearer ${newToken?.jtiToken || newToken.token}`);
        setAuthorizationHeader(newToken?.jtiToken || newToken.token);
      } catch (error) {
        alerts.error("You've been signed out");
        return authnClient.signOut().then(() => req);
      }
    }

    return req;
  };

  //@ts-ignore
  const _handle401Error = async (response: Response): Promise<Response> => {
    if (!response.ok && response.status === 401) {
      alerts.error('You have been signed out due to inactivity. Please sign in again');
      location.href = '/logout';
    }
    return response;
  };

  useEffect(() => {
    if (currentLocation?.locationId) {
      setLocationIdHeader(currentLocation.locationId);
    }
  }, [currentLocation, setLocationIdHeader]);

  const setupHttp = useCallback(
    (token?: string) => {
      if (setupCalled.current) return;

      setupCalled.current = true;

      if (currentLocation?.locationId) {
        setLocationIdHeader(currentLocation.locationId);
      }

      // useMiddleware(refreshTokenMiddleware);
      setAuthorizationHeader(token ?? '');
      // useErrorMiddleware(handle401Error);

      // return () => {
      //   clearMiddleware();
      //   clearErrorMiddleware();
      // };
    },
    [
      currentLocation,
      setLocationIdHeader,
      setAuthorizationHeader,
      // useMiddleware,
      // useErrorMiddleware,
      // clearMiddleware,
      // clearErrorMiddleware,
    ]
  );

  return setupHttp;
};
